import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  X,
  AlertCircle,
  Loader2,
  Shield,
  Users,
  Lock
} from 'lucide-react';
import { usePermissions } from '../contexts/PermissionsContext';
import adminRbacService from '../services/adminRbacService';

const RolesManagement = () => {
  const permissions = usePermissions();
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissionKeys: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [editingRole, setEditingRole] = useState(null);

  const hasPermission = (key) => !key || permissions.includes(key) || permissions.includes('*');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, permsData] = await Promise.all([
        adminRbacService.getRoles(),
        adminRbacService.getPermissions()
      ]);
      setRoles(rolesData);
      setAllPermissions(permsData);
      if (rolesData.length > 0 && !selectedRole) {
        setSelectedRole(rolesData[0]);
      }
      return rolesData; // Return fresh data for use in handlers
    } catch (err) {
      setError(err.message || 'Failed to load data');
      return []; // Return empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setModalMode('create');
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissionKeys: []
    });
    setShowModal(true);
  };

  const handleEditClick = (role) => {
    setModalMode('edit');
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissionKeys: role.permissions.map(p => p.key)
    });
    setShowModal(true);
  };

  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
    setShowDeleteConfirm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        permissionKeys: formData.permissionKeys
      };

      if (modalMode === 'create') {
        await adminRbacService.createRole(payload);
        const freshRoles = await loadData();
        // Auto-select the newly created role
        const newRole = freshRoles.find(r => r.name === formData.name);
        if (newRole) setSelectedRole(newRole);
      } else {
        await adminRbacService.updateRole(editingRole.id, payload);
        const freshRoles = await loadData();
        // If we were editing the selected role, update it to the fresh version
        if (editingRole && selectedRole && editingRole.id === selectedRole.id) {
          const updatedRole = freshRoles.find(r => r.id === editingRole.id);
          if (updatedRole) setSelectedRole(updatedRole);
        }
      }

      setShowModal(false);
      setEditingRole(null);
    } catch (err) {
      setError(err.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;

    setSubmitting(true);
    setError(null);

    try {
      await adminRbacService.deleteRole(roleToDelete.id);
      const freshRoles = await loadData();
      setShowDeleteConfirm(false);
      setRoleToDelete(null);
      if (selectedRole && selectedRole.id === roleToDelete.id) {
        setSelectedRole(freshRoles.length > 0 ? freshRoles[0] : null);
      }
    } catch (err) {
      setError(err.message || 'Failed to delete role');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePermission = (permissionKey) => {
    setFormData(prev => ({
      ...prev,
      permissionKeys: prev.permissionKeys.includes(permissionKey)
        ? prev.permissionKeys.filter(k => k !== permissionKey)
        : [...prev.permissionKeys, permissionKey]
    }));
  };

  // Group permissions by category
  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    const category = perm.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(perm);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Roles Management</h1>
          <p className="text-gray-600 mt-1">Manage roles and permissions</p>
        </div>
        {hasPermission('roles.manage') && (
          <button
            onClick={handleCreateClick}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Role
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-medium text-red-900">Error</h3>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-400 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Content: Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel: Roles List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-900">Roles</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {roles.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No roles found
                </div>
              ) : (
                roles.map((role) => (
                  <div
                    key={role.id}
                    onClick={() => setSelectedRole(role)}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedRole && selectedRole.id === role.id
                        ? 'bg-primary-50 border-l-4 border-primary-600'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <h3 className="font-medium text-gray-900 truncate">
                            {role.name}
                          </h3>
                        </div>
                        {role.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {role.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {role.isSystem && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                              <Lock className="w-3 h-3" />
                              System
                            </span>
                          )}
                          <span className="text-xs text-gray-500">
                            {role.permissions?.length || 0} permissions
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right Panel: Role Details */}
        <div className="lg:col-span-2">
          {selectedRole ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900">
                        {selectedRole.name}
                      </h2>
                      {selectedRole.isSystem && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                          <Lock className="w-3 h-3" />
                          System Role
                        </span>
                      )}
                    </div>
                    {selectedRole.description && (
                      <p className="text-gray-600">{selectedRole.description}</p>
                    )}
                  </div>
                  {hasPermission('roles.manage') && (
                    <div className="flex gap-2">
                      {!selectedRole.isSystem && (
                        <button
                          onClick={() => handleEditClick(selectedRole)}
                          className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                      )}
                      {!selectedRole.isSystem && (
                        <button
                          onClick={() => handleDeleteClick(selectedRole)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Permissions</h3>

                {!selectedRole.permissions || selectedRole.permissions.length === 0 ? (
                  <p className="text-gray-500">No permissions assigned</p>
                ) : (
                  <div className="space-y-6">
                    {Object.entries(
                      selectedRole.permissions.reduce((acc, perm) => {
                        const category = perm.category || 'Other';
                        if (!acc[category]) {
                          acc[category] = [];
                        }
                        acc[category].push(perm);
                        return acc;
                      }, {})
                    ).map(([category, perms]) => (
                      <div key={category}>
                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                          <div className="w-1 h-4 bg-primary-600 rounded"></div>
                          {category}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-3">
                          {perms.map((perm) => (
                            <div
                              key={perm.id}
                              className="flex items-start gap-2 p-2 bg-gray-50 rounded-lg"
                            >
                              <div className="w-5 h-5 bg-green-100 rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">
                                  {perm.name}
                                </div>
                                <div className="text-xs text-gray-500 font-mono">
                                  {perm.key}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select a role to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {modalMode === 'create' ? 'Create New Role' : 'Edit Role'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingRole(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g. Content Manager"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Brief description of this role"
                  />
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Permissions</h3>
                <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto space-y-4">
                  {Object.entries(groupedPermissions).map(([category, perms]) => (
                    <div key={category}>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <div className="w-1 h-4 bg-primary-600 rounded"></div>
                        {category}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-3">
                        {perms.map((perm) => (
                          <label
                            key={perm.id}
                            className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={formData.permissionKeys.includes(perm.key)}
                              onChange={() => togglePermission(perm.key)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 mt-0.5"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-gray-900">
                                {perm.name}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {perm.key}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  {formData.permissionKeys.length} permission(s) selected
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingRole(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modalMode === 'create' ? 'Create Role' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && roleToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Role</h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to delete the role <span className="font-medium">{roleToDelete.name}</span>? This action cannot be undone and will affect all users assigned to this role.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setRoleToDelete(null);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesManagement;
